import { useState, useCallback } from 'react'
import Cropper from 'react-easy-crop'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap'

// ─── Canvas helper (for final file output only) ──────────────────────────────

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.setAttribute('crossOrigin', 'anonymous')
    img.src = url
  })

const cropToBlob = (imageSrc, pixelCrop) =>
  new Promise(async (resolve) => {
    const image = await createImage(imageSrc)
    const canvas = document.createElement('canvas')
    const outW = 400
    const outH = 300 // 4:3
    canvas.width = outW
    canvas.height = outH
    const ctx = canvas.getContext('2d')

    // Fill with white background — handles areas outside the image when zoomed out
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, outW, outH)

    const imgW = image.naturalWidth
    const imgH = image.naturalHeight

    // Clamp source region to the actual image bounds
    const srcX = Math.max(0, pixelCrop.x)
    const srcY = Math.max(0, pixelCrop.y)
    const srcX2 = Math.min(imgW, pixelCrop.x + pixelCrop.width)
    const srcY2 = Math.min(imgH, pixelCrop.y + pixelCrop.height)
    const srcW = srcX2 - srcX
    const srcH = srcY2 - srcY

    if (srcW > 0 && srcH > 0) {
      // Scale the clamped source rect onto the proportional region of the canvas
      const scaleX = outW / pixelCrop.width
      const scaleY = outH / pixelCrop.height
      const dstX = (srcX - pixelCrop.x) * scaleX
      const dstY = (srcY - pixelCrop.y) * scaleY
      const dstW = srcW * scaleX
      const dstH = srcH * scaleY
      ctx.drawImage(image, srcX, srcY, srcW, srcH, dstX, dstY, dstW, dstH)
    }

    canvas.toBlob(
      (blob) =>
        resolve(new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' })),
      'image/jpeg',
      0.92,
    )
  })

// ─── CSS-based live preview ───────────────────────────────────────────────────
// Uses the percentage-based croppedArea from react-easy-crop to position the
// source image directly with CSS — no canvas, no async, pixel-perfect at any
// source aspect ratio (provably correct for square crops).

const CropPreview = ({
  imageSrc,
  croppedArea,
  width,
  height,
  label,
  borderRadius = 8,
}) => {
  const placeholder = (
    <div
      style={{
        width,
        height,
        borderRadius,
        overflow: 'hidden',
        border: '2px solid #dee2e6',
        background: '#f8f9fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <i className='fa fa-image text-muted' style={{ fontSize: height / 3 }} />
    </div>
  )

  if (!croppedArea) return placeholder

  const { x, y, width: cropW, height: cropH } = croppedArea

  // Scale so the crop region fills the preview container exactly.
  // width% of the source fills 100% of the container horizontally.
  // height% of the source fills 100% of the container vertically.
  // These are independent because the crop is 4:3 on a non-square source.
  const imgStyle = {
    position: 'absolute',
    width: `${10000 / cropW}%`,
    height: `${10000 / cropH}%`,
    maxWidth: 'none',
    maxHeight: 'none',
    left: `${-(x / cropW) * 100}%`,
    top: `${-(y / cropH) * 100}%`,
  }

  return (
    <div>
      {label && (
        <p className='text-muted mb-1' style={{ fontSize: 11 }}>
          {label}
        </p>
      )}
      <div
        style={{
          width,
          height,
          borderRadius,
          overflow: 'hidden',
          border: '2px solid #dee2e6',
          position: 'relative',
          background: '#f8f9fa',
        }}
      >
        <img src={imageSrc} alt='preview' style={imgStyle} />
      </div>
    </div>
  )
}

// ─── Component ───────────────────────────────────────────────────────────────

const ThumbnailCropperModal = ({ imageSrc, onCropComplete, onCancel }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
  const [croppedArea, setCroppedArea] = useState(null)
  const [isApplying, setIsApplying] = useState(false)

  const onCropCompleteCallback = useCallback((area, pixels) => {
    setCroppedArea(area)
    setCroppedAreaPixels(pixels)
  }, [])

  const handleApply = async () => {
    if (!croppedAreaPixels) return
    setIsApplying(true)
    const file = await cropToBlob(imageSrc, croppedAreaPixels)
    onCropComplete(file)
    setIsApplying(false)
  }

  return (
    <Modal isOpen toggle={onCancel} size='lg' backdrop='static' zIndex={9999}>
      <ModalHeader toggle={onCancel}>
        <i className='fa fa-crop mr-2' />
        Edit Thumbnail
      </ModalHeader>

      <ModalBody>
        <div className='row'>
          {/* Cropper */}
          <div className='col-8'>
            <div
              style={{
                position: 'relative',
                height: 320,
                background: '#1a1a2e',
                borderRadius: 8,
                overflow: 'hidden',
              }}
            >
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={4 / 3}
                minZoom={0.1}
                maxZoom={3}
                restrictPosition={false}
                onCropChange={setCrop}
                onCropComplete={onCropCompleteCallback}
                onZoomChange={setZoom}
                showGrid={true}
                style={{
                  containerStyle: { borderRadius: 8 },
                  cropAreaStyle: { border: '2px solid #37c2de' },
                }}
              />
            </div>

            {/* Zoom slider */}
            <div className='d-flex align-items-center mt-3 px-1'>
              <i
                className='fa fa-search-minus text-muted mr-2'
                style={{ fontSize: 14 }}
              />
              <input
                type='range'
                min={0.1}
                max={3}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                style={{ flex: 1, accentColor: '#37c2de' }}
              />
              <i
                className='fa fa-search-plus text-muted ml-2'
                style={{ fontSize: 14 }}
              />
              <span className='ml-2 text-muted small' style={{ minWidth: 36 }}>
                {zoom.toFixed(1)}×
              </span>
            </div>

            <p className='text-muted small mt-2 mb-0'>
              <i className='fa fa-info-circle mr-1' />
              Drag to reposition · Scroll or use slider to zoom
            </p>
          </div>

          {/* Live Preview */}
          <div className='col-4'>
            <p className='font-weight-bold mb-3' style={{ fontSize: 13 }}>
              Live Preview
            </p>

            <CropPreview
              imageSrc={imageSrc}
              croppedArea={croppedArea}
              width={160}
              height={120}
              label='Card view:'
              borderRadius={8}
            />

            <div className='mt-3'>
              <CropPreview
                imageSrc={imageSrc}
                croppedArea={croppedArea}
                width={64}
                height={48}
                label='Icon view:'
                borderRadius={6}
              />
            </div>
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button
          color='primary'
          onClick={handleApply}
          disabled={!croppedAreaPixels || isApplying}
        >
          {isApplying ? (
            <>
              <i className='fa fa-spinner fa-spin mr-1' /> Applying...
            </>
          ) : (
            <>
              <i className='fa fa-check mr-1' /> Apply Crop
            </>
          )}
        </Button>
        <Button color='secondary' outline onClick={onCancel}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ThumbnailCropperModal
